import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { isEmpty, orderBy } from "lodash-es";
import { useMemo } from "react";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { notEmpty } from "../../../../utils/not-empty";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { prettyChannelName } from "../../../channels/utils";
import { usePersonsInSegmentJson } from "./moment-segment-preview/hooks/use-persons-in-segment-json";

import type { ChannelName, ChannelStatus } from "../../../channels/types";
import type { GetPersonsInSegmentJsonPerson, Segment } from "../../types/segment-schema";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import type { FC } from "react";

interface SegmentGroupProps {
  segment: Segment["segment_json"] | Segment["segment_json"]["segments"][number];
  channels: ChannelName[];
  type: "full" | "group";
}

export const SegmentPreview: FC<SegmentGroupProps> = ({ segment, channels, type }) => {
  const dialog = useDialog<ChannelName>();

  const { data, error, loading } = usePersonsInSegmentJson(
    segment.type === "segment" && segment.segments.length === 0 ? null : { segment_json: segment },
  );

  const orderedChannels = useMemo(
    () =>
      orderBy(channels, (c) => {
        switch (c) {
          case "work_email":
            return 1;
          case "slack":
            return 2;
          case "teams":
            return 3;
          case "twilio":
            return 4;
          case "personal_email":
            return 5;
          case "whatsapp":
            return 6;
          case null:
            return 7;
        }
        return undefined;
      })
        .map((c) => {
          if (!c) return null;

          return {
            name: c,
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            persons: data?.filter((p) => (p.channels as Record<string, ChannelStatus>)[c]) ?? [],
          };
        })
        .filter(notEmpty),
    [channels, data],
  );

  if (error) {
    console.error("Error loading persons for segment:", error);
    return (
      <Alert severity="error">
        {type === "group" ? "Error loading people in group" : "Error loading people in segment"}
      </Alert>
    );
  }

  if (loading)
    return <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />;

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
        <Typography variant="body2">
          {type === "group" && (
            <>
              There {data?.length === 1 ? "is " : "are "} currently{" "}
              <span
                data-analytics-id="people-in-segment-count"
                style={{ cursor: "pointer", color: "#5048e5" }}
                onClick={() => dialog.handleOpen(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") dialog.handleOpen(null);
                }}
                role="link"
                tabIndex={0}
              >
                {data?.length ?? 0}
                {data?.length === 1 ? " person" : " people"} in this group
              </span>
            </>
          )}
          {type === "full" && (
            <>
              In total, there {data?.length === 1 ? "is " : "are "}
              <span
                data-analytics-id="people-in-segment-count"
                style={{ cursor: "pointer", color: "#5048e5" }}
                onClick={() => dialog.handleOpen(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") dialog.handleOpen(null);
                }}
                role="link"
                tabIndex={0}
              >
                {data?.length ?? 0}
                {data?.length === 1 ? " person" : " people"} in this segment
              </span>
            </>
          )}
        </Typography>

        {orderedChannels.map((channel) => (
          <Stack
            key={channel.name}
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", mr: 2 }}
          >
            <Button
              variant="text"
              onClick={() => dialog.handleOpen(channel.name)}
              disabled={channel.persons.length === 0}
              startIcon={<ChannelIcon channel={channel.name} fontSize="small" />}
              data-analytics-id={`view-recipients-${channel.name}-button`}
            >
              {channel.persons.length}
            </Button>
          </Stack>
        ))}
      </Stack>
      <Dialog
        open={dialog.open}
        onClose={dialog.handleClose}
        title="Edit Segment"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>People in Segment Group: {prettyChannelName(dialog.data ?? null)}</DialogTitle>
        <DialogContent>
          <SegmentPreviewDialogDataGrid
            channelName={dialog.data ?? null}
            persons={
              dialog.data
                ? (orderedChannels.find((c) => c.name === dialog.data!)?.persons ?? [])
                : (data ?? [])
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={dialog.handleClose} data-analytics-id="close-modal-button">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SegmentPreviewDialogDataGrid: FC<{
  channelName: ChannelName;
  persons: GetPersonsInSegmentJsonPerson[];
}> = ({ channelName, persons }) => {
  const columns: GridColDef<GetPersonsInSegmentJsonPerson>[] = useMemo(
    () => [
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
      },
      {
        field: "channels",
        headerName: channelName ? "Channel Display Name" : "Channels",
        flex: 1,
        disableExport: isEmpty(channelName),
        valueFormatter: (value: Record<string, ChannelStatus>) => {
          if (!channelName) return "";
          return value[channelName]?.display || "";
        },
        renderCell: (
          params: GridRenderCellParams<
            GetPersonsInSegmentJsonPerson,
            GetPersonsInSegmentJsonPerson["channels"]
          >,
        ) => {
          if (channelName)
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            return (params.value as Record<string, ChannelStatus>)[channelName]?.display || "";

          return (
            <Stack direction="row" sx={{ alignItems: "center", height: "100%" }}>
              {channels.map((channel) => {
                // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                const channelInfo = (params.value as Record<string, ChannelStatus>)[channel];
                return channelInfo ? (
                  <Tooltip key={channel} title={channelInfo.display}>
                    <span>
                      <ChannelIcon channel={channel} fontSize="small" />
                    </span>
                  </Tooltip>
                ) : null;
              })}
            </Stack>
          );
        },
      },
    ],
    [channelName],
  );

  return <DataGridPro rows={persons} columns={columns} pagination showToolbar />;
};

const channels = ["slack", "teams", "work_email", "personal_email", "twilio"] as const;
